import React from 'react'
import ThreeSixtyViewer from '../../3_Organisms/360Viewer/360Viewer'
import CalloutBoxShort from '../../2_Molecules/CalloutBox/CalloutBoxShort';
import { languageController } from '../../../controllers/language/languageController';

interface ExteriorImage {
  src: string;
}

interface ThreeSixtyData {
  start_index: number;
  end_index: number;
  file_path: string;
  filename_prefix: string;
  filename_suffix: string;
}

interface DefaultExterior {
  code: string;
}

interface Interior {
  code: string;
  default_exterior: DefaultExterior;
  filename: string;
  include_in_360?: boolean;
}

interface Exterior {
  [key: string]: {
    '360': ThreeSixtyData;
  };
}

interface VehicleVisual {
  interiors: Interior[];
  exteriors: Exterior;
  static_content_base_path: string;
}

interface VehicleData {
  visual?: VehicleVisual;
  title: string;
  model: string;
  modelYear: string;
  description?: string;
  msrp?: number;
}

interface DealerGeolocation {
  distance: number;
  latitude: number;
  longitude: number;
}

interface Dealer {
  name: string;
  address_line_1: string;
  city: string;
  post_code: string;
  website?: string;
  phone_number?: string;
  geolocation: DealerGeolocation;
  dealer_code?: string;
}

interface DealerData {
  dealer?: Dealer;
  userPosition?: UserPosition;
}

interface UserPosition {
  lat: number;
  lng: number;
}

interface DateAndTime {
  date: string;
  time: string;
}

interface ContactDetails {
  firstname: string;
  lastname: string;
  email: string;
  postalcode: string;
  phone?: string;
  preferredContact?: string;
  message?: string;
  optin?: string;
}

interface ConfirmationStepProps {
  vehicleData?: VehicleData;
  dateAndTime?: DateAndTime;
  dealerData?: DealerData;
  contactDetails?: ContactDetails;
  displayPrice?: boolean;
}

// Extend window interface for bnp_retailer_mode
declare global {
  interface Window {
    bnp_retailer_mode?: boolean;
  }
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  vehicleData,
  dateAndTime,
  dealerData,
  contactDetails,
  displayPrice = true
}) => {
  const { dealer: selectedDealerData, userPosition } = dealerData || {};
  const { visual } = vehicleData || {};

  // Create an array of exterior images of the model.
  const generateExteriorImages = (): ExteriorImage[] => {
    if (!visual) return [];

    try {
      const { interiors, exteriors, static_content_base_path } = visual;

      const defaultExteriorCode = interiors[0]?.default_exterior.code;
      const interior = interiors[0];
      const intCode = interior?.code;

      if (defaultExteriorCode && exteriors[defaultExteriorCode]) {
        const threeSixtyData = exteriors[defaultExteriorCode]['360'];

        const { start_index, end_index, file_path, filename_prefix, filename_suffix } = threeSixtyData;
        let imagesArr: ExteriorImage[] = [];

        for (let index = start_index; index <= end_index; index++) {
          let path: string;

          if(interior && interior.include_in_360)
            path = static_content_base_path + file_path + `${filename_prefix.replace(" ", "-")}${intCode}-` + index + filename_suffix;
          else
            path = static_content_base_path + file_path + filename_prefix + index + filename_suffix;

          imagesArr.push({ src: path });
        }

        return imagesArr;
      }
    } catch (err) {
      console.log(err)
    }

    return [];
  }

  const generateInteriorImage = (): string => {
    if (!visual) return '';
    const { interiors, static_content_base_path } = visual;
    return static_content_base_path + (interiors[0]?.filename || '');
  }

  return (
    <div className="mz-confirmation-step" style={!window.bnp_retailer_mode ? {} : {textAlign: 'center'}}>
      <div className="mz-container">
        <div className="mz-full-width">

          <div className={!window.bnp_retailer_mode ? "mz-confirmation-step__top" : ""}>
            <h4>{languageController.getCopy('confirmationStep', 'title') || ''}, {contactDetails?.firstname}!</h4>
            {!window.bnp_retailer_mode ?
              <p>{languageController.getCopy('confirmationStep', 'subTitle') || ''}</p>
            :
              <p><strong>{languageController.getCopy('confirmationStep', 'subTitleRetailer') || ''}</strong></p>
            } 
          </div>

          
          <div className={!window.bnp_retailer_mode ?"mz-confirmation-step__main" : ""}>
            {!window.bnp_retailer_mode &&
              <div className="mz-confirmation-step__360">
                <ThreeSixtyViewer
                  exteriorImages={generateExteriorImages()}
                  interiorPanoramaImage={generateInteriorImage()}
                />
              </div>
            }
            <div className="mz-confirmation-step__callout">
              <CalloutBoxShort
                vehicleData={vehicleData}
                dealerData={selectedDealerData}
                userPosition={userPosition}
                dateAndTimeData={dateAndTime}
                displayPrice={displayPrice}
              />
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}

export default ConfirmationStep
